import React from 'react'
import { Typography, Select, MenuItem, Tooltip, IconButton } from '@material-ui/core'
import WrapTextIcon from '@material-ui/icons/WrapText'
import ShortTextIcon from '@material-ui/icons/ShortText'
import { AlignRow, StepperRow, HexColorInput, commonValue as common, MIN_WRAP_LINES, MAX_WRAP_LINES } from './panelControls'
import {
  SUPPORTED_FONTS, fontOptionsFor, weightOptionsFor,
  opacityPercentOfColor, rgbOfColor, withOpacityPercent, withRgb,
} from './overlayEditorControls'
import { lineHeightPct } from './placementModel'

// Text style controls for one or many text fields. `targets` is [{ placement, field }];
// `onPatch(fn)` applies fn(placement) -> patch to every target, so each keeps its own
// values for whatever the change doesn't set (a color's opacity, a box's height).
export default function TextStyleControls({ targets, onPatch }) {
  const familyOf = ({ placement }) => placement.fontFamily ?? SUPPORTED_FONTS[0]
  const weightOf = (t) => {
    const weight = t.placement.fontWeight
    return weightOptionsFor(familyOf(t)).includes(weight) ? weight : 400
  }
  const firstPlacement = targets[0].placement

  const family = common(targets, familyOf)
  const fonts = SUPPORTED_FONTS.filter((name) => targets.every((t) => fontOptionsFor(t.field).includes(name)))
  const fontList = family && !fonts.includes(family) ? [...fonts, family] : fonts

  const weightSets = targets.map((t) => weightOptionsFor(familyOf(t)))
  const weights = weightSets[0].filter((w) => weightSets.every((set) => set.includes(w)))
  const weight = common(targets, weightOf)

  const size = common(targets, (t) => t.placement.fontSize ?? 32)
  const color = common(targets, (t) => t.placement.color ?? '#ffffff')
  const opacity = common(targets, (t) => opacityPercentOfColor(t.placement.color))
  const hAlign = common(targets, (t) => t.placement.hAlign ?? 'center')
  const vAlign = common(targets, (t) => t.placement.vAlign ?? 'middle')
  const fit = common(targets, (t) => t.placement.fit ?? 'wrap')
  const wrapping = targets.filter((t) => (t.placement.fit ?? 'wrap') === 'wrap')
  const lines = wrapping.length ? common(wrapping, (t) => t.placement.maxLines ?? MIN_WRAP_LINES) : undefined

  const selectProps = { fullWidth: true, displayEmpty: true, renderValue: (v) => v || 'Mixed', style: { fontSize: 12 } }

  return (
    <>
      <div style={{ padding: '4px 12px' }}>
        <Typography variant='caption' color='textSecondary'>Font family</Typography>
        <Select
          {...selectProps}
          value={family ?? ''}
          onChange={(e) => {
            const next = e.target.value
            const options = weightOptionsFor(next)
            // A weight outside the new font's range (Roboto/900 -> JetBrains Mono, which
            // tops out at 800) is clamped to the nearest valid step.
            onPatch((p) => (options.includes(p.fontWeight)
              ? { fontFamily: next }
              : { fontFamily: next, fontWeight: options[options.length - 1] }))
          }}
        >
          {fontList.map((name) => (
            <MenuItem key={name} value={name} style={{ fontSize: 12 }}>{name}</MenuItem>
          ))}
        </Select>
      </div>
      <div style={{ padding: '4px 12px' }}>
        <Typography variant='caption' color='textSecondary'>Font weight</Typography>
        <Select
          {...selectProps}
          value={weight ?? ''}
          onChange={(e) => onPatch(() => ({ fontWeight: e.target.value }))}
        >
          {weights.map((w) => (
            <MenuItem key={w} value={w} style={{ fontSize: 12 }}>{w}</MenuItem>
          ))}
        </Select>
      </div>
      <StepperRow
        label='Size'
        value={size ?? (firstPlacement.fontSize ?? 32)}
        mixed={size === undefined}
        suffix='px'
        step={1}
        onChange={(v) => {
          const next = Math.max(1, Math.round(v))
          // Grow the box to fit the font (never shrink a hand-sized box) so vertical
          // alignment has room — otherwise a large glyph overflows a short box and
          // vAlign has no visible effect.
          onPatch((p) => ({ fontSize: next, h: Math.max(p.h ?? 0, lineHeightPct(next)) }))
        }}
      />
      <div style={{ padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Typography variant='caption' color='textSecondary'>Color</Typography>
        <input
          type='color'
          // The picker edits only the RGB: it cannot express alpha, so each field's own
          // opacity is carried through the change.
          value={rgbOfColor(color ?? firstPlacement.color)}
          onChange={(e) => onPatch((p) => ({ color: withRgb(p.color ?? '#ffffff', e.target.value) }))}
          style={{ border: 'none', cursor: 'pointer', width: 32, height: 24 }}
        />
        <HexColorInput
          value={color ?? ''}
          placeholder='Mixed'
          onChange={(v) => onPatch(() => ({ color: v }))}
        />
      </div>
      <StepperRow
        label='Opacity'
        value={opacity ?? opacityPercentOfColor(firstPlacement.color)}
        mixed={opacity === undefined}
        suffix='%'
        step={1}
        onChange={(v) => onPatch((p) => ({ color: withOpacityPercent(p.color ?? '#ffffff', v) }))}
      />
      <AlignRow hAlign={hAlign ?? null} vAlign={vAlign ?? null} onChange={(patch) => onPatch(() => patch)} />
      <div style={{ padding: '4px 12px', display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant='caption' color='textSecondary' style={{ minWidth: 32 }}>Fit</Typography>
        {[
          { key: 'wrap', Icon: WrapTextIcon, title: 'Wrap' },
          { key: 'trim', Icon: ShortTextIcon, title: 'Trim' },
        ].map(({ key, Icon, title }) => {
          const active = fit === key
          return (
            <Tooltip key={key} title={title} placement='top'>
              <IconButton
                size='small'
                onClick={() => onPatch((p) => (key === 'wrap'
                  ? { fit: key, maxLines: p.maxLines ?? MIN_WRAP_LINES }
                  : { fit: key }))}
                style={{
                  padding: 4,
                  color: active ? '#fff' : '#888',
                  background: active ? '#1976d2' : 'transparent',
                  borderRadius: 4,
                }}
              >
                <Icon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )
        })}
      </div>
      {/* Wrapping always carries a cap: it clamps and ellipsises, where an uncapped wrap
          lets the box's overflow slice a line in half. One line is what Trim already
          does, so the count starts at two. */}
      {wrapping.length > 0 && (
        <StepperRow
          label='Lines'
          value={lines ?? (wrapping[0].placement.maxLines ?? MIN_WRAP_LINES)}
          mixed={lines === undefined}
          step={1}
          onChange={(v) => {
            const next = Math.max(MIN_WRAP_LINES, Math.min(MAX_WRAP_LINES, Math.round(v)))
            onPatch((p) => ((p.fit ?? 'wrap') === 'wrap' ? { maxLines: next } : {}))
          }}
        />
      )}
    </>
  )
}
